import { up } from 'styled-breakpoints'
import { useBreakpoint } from 'styled-breakpoints/react-styled'
import { startTransition, useContext, useEffect, useState } from 'react'
import { ThemeContext } from 'styled-components'
import { isServer } from 'utils/runtimeUtils'
import { type DeviceType, useUserDeviceData } from 'services/UserDeviceContext'

// This should stay false forever in the server, and change to true in client only
// Used to prevent a flicker after the first render. Once we hydrated we never want
// to use the server results again.
let clientBased = false

export type ScreenSizeBreakpoint = 'sm' | 'md' | 'lg' | 'xxl'
const breakpointToDeviceTypes: Record<ScreenSizeBreakpoint, DeviceType[]> = {
  sm: ['mobile', 'tablet', 'browser'],
  md: ['tablet', 'browser'],
  lg: ['browser'],
  // We can't detect xxl screen from user agent, so we opt to say it's not xxl on the server
  xxl: [],
}

export default function useScreenSize(breakpoint: ScreenSizeBreakpoint) {
  const { deviceType } = useUserDeviceData()
  const [isClientBased, setIsClientBased] = useState(clientBased)
  const themeContext = useContext(ThemeContext)
  const match = useBreakpoint(up(breakpoint))

  // This makes it so if there is a difference between the browser and the server about
  // whether the screen is large, we will use the server version in the first render.
  // This prevent possible hydration errors, since the client needs to render the exact
  // same thing the server did. On the second render this will flicker to client calculation.
  useEffect(() => {
    startTransition(() => {
      setIsClientBased(true)
      clientBased = true
    })
  }, [])

  if (isServer() || !isClientBased) {
    return breakpointToDeviceTypes[breakpoint].includes(deviceType)
  }

  if (match === null) {
    if (!themeContext) throw new Error('Missing themeConext')

    const viewportWidth = window.visualViewport
      ? window.visualViewport.width
      : document.body.clientWidth
    return viewportWidth >= themeContext.breakpoints[breakpoint]
  }

  return match
}

export const useIsScreenHeight = (minHeight: number) => {
  const [isClientBased, setIsClientBased] = useState(clientBased)
  const [isAboveBreakpoint, setIsAboveBreakpoint] = useState(false)

  useEffect(() => {
    startTransition(() => {
      setIsClientBased(true)
      clientBased = true
    })
  }, [])

  useEffect(() => {
    const handleResize = () => {
      const viewportHeight = window.visualViewport
        ? window.visualViewport.height
        : document.body.clientHeight
      setIsAboveBreakpoint(viewportHeight >= minHeight)
    }
    handleResize()

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [minHeight])

  if (isServer() || !isClientBased) {
    return false
  }

  return isAboveBreakpoint
}
